import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {SummaryType, EntitySummaryStatus} from 'nutshell-graphql-types';
import type {
    GetEntitySummary as GetEntitySummaryQuery,
    EntitySummaryFragment,
} from 'nutshell-graphql-types';

import GET_ENTITY_SUMMARY from '../queries/get-entity-summary.graphql';

export const useGetEntitySummary = (
    entityId: string,
    summaryType: SummaryType
): {
    entitySummary: EntitySummaryFragment | null | undefined;
    isLoading: boolean;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    isRefetching: boolean;
    startPolling: (arg1: number) => void;
    stopPolling: () => void;
} => {
    const {data, error, loading, startPolling, stopPolling, refetch, networkStatus} = useQuery<
        GetEntitySummaryQuery,
        Record<any, any>
    >(GET_ENTITY_SUMMARY, {
        fetchPolicy: 'cache-and-network',
        variables: {
            id: entityId,
            summaryType,
        },
    });

    const entitySummary = data ? data.entitySummary : null;

    React.useEffect(() => {
        const status: EntitySummaryStatus | null | undefined = entitySummary
            ? entitySummary.status
            : null;

        // Start polling if the entitySummary status is in progress
        if (status === 'LOADING') {
            startPolling(2000); // 2 seconds
            // @ts-expect-error
        } else if (status !== 'LOADING') {
            stopPolling();
        }
    }, [entitySummary, startPolling, stopPolling]);

    return {
        entitySummary,
        // https://github.com/apollographql/apollo-client/blob/main/src/core/networkStatus.ts
        isLoading: loading && networkStatus !== 4, // refetching is 4
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        refetch,
        isRefetching: networkStatus === 4,
        startPolling,
        stopPolling,
    };
};
